import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface BusinessConfig {
  inventory_rotation_settings: {
    prioritize_slow_moving: boolean;
    rotation_analysis_days: number;
    cross_company_enabled: boolean;
    auto_transfer_threshold: number;
  };
  sales_flow_settings: {
    enable_multi_branch_sales: boolean;
    payment_options: string[];
    delivery_options: string[];
    commission_cross_branch: boolean;
  };
  stock_validation_settings: {
    show_group_companies: boolean;
    enable_supplier_orders: boolean;
    advance_order_enabled: boolean;
    transfer_approval_required: boolean;
  };
  notification_settings: {
    whatsapp_enabled: boolean;
    email_enabled: boolean;
    realtime_updates: boolean;
    alert_thresholds: {
      low_stock: number;
      slow_moving_days: number;
    };
  };
}

interface BusinessOperationsContextType {
  config: BusinessConfig | null;
  loading: boolean;
  updateConfig: (section: keyof BusinessConfig, newSettings: any) => Promise<void>;
  realtimeData: {
    stock_updates: any[];
    transfers: any[];
    sales: any[];
    notifications: any[];
  };
}

const defaultConfig: BusinessConfig = {
  inventory_rotation_settings: {
    prioritize_slow_moving: true,
    rotation_analysis_days: 30,
    cross_company_enabled: true,
    auto_transfer_threshold: 5
  },
  sales_flow_settings: {
    enable_multi_branch_sales: true,
    payment_options: ['pickup_pay', 'origin_pay', 'full_pay'],
    delivery_options: ['pickup', 'home_free', 'home_express'],
    commission_cross_branch: true
  },
  stock_validation_settings: {
    show_group_companies: true,
    enable_supplier_orders: true,
    advance_order_enabled: true,
    transfer_approval_required: false
  },
  notification_settings: {
    whatsapp_enabled: true,
    email_enabled: true,
    realtime_updates: true,
    alert_thresholds: {
      low_stock: 10,
      slow_moving_days: 15
    }
  }
};

const BusinessOperationsContext = createContext<BusinessOperationsContextType | undefined>(undefined);

export function BusinessOperationsProvider({ children }: { children: ReactNode }) {
  const authContext = useAuth();
  
  // Early return if auth context is not available (prevents hot reload issues)
  if (!authContext) {
    return <div className="flex items-center justify-center min-h-screen">Inicializando configuración...</div>;
  }
  
  const { selectedCompany } = authContext;
  const [config, setConfig] = useState<BusinessConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [realtimeData, setRealtimeData] = useState({
    stock_updates: [],
    transfers: [],
    sales: [],
    notifications: []
  });

  // Load dynamic configuration from database
  const loadConfig = async () => {
    if (!selectedCompany?.id) return;

    try {
      const { data: companyConfig, error: fetchError } = await supabase
        .from('company_business_config')
        .select('*')
        .eq('company_id', selectedCompany.id)
        .maybeSingle();

      if (fetchError) {
        console.warn('⚠️ [Context] Error fetching config:', fetchError);
        console.log('⚠️ [Context] Using default config due to fetch error');
        setConfig(defaultConfig);
      } else if (companyConfig) {
        console.log('✅ [Context] Config loaded from database');
        setConfig(companyConfig.configuration as any as BusinessConfig);
      } else {
        console.log('⚠️ [Context] No config found, attempting to create default...');
        // Try to create default config for company
        const { data: newConfig, error: insertError } = await supabase
          .from('company_business_config')
          .insert({
            company_id: selectedCompany.id,
            configuration: defaultConfig as any,
            is_active: true
          })
          .select()
          .single();

        if (insertError) {
          console.warn('⚠️ [Context] Could not create config (RLS restriction):', insertError.message);
          console.log('⚠️ [Context] Using default config in memory only');
          setConfig(defaultConfig);
        } else if (newConfig && newConfig.configuration) {
          console.log('✅ [Context] New config created successfully');
          setConfig(newConfig.configuration as any as BusinessConfig);
        } else {
          console.warn('⚠️ [Context] Config creation returned null, using default');
          setConfig(defaultConfig);
        }
      }
    } catch (error) {
      console.error('Error loading business config:', error);
      setConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  // Update configuration dynamically
  const updateConfig = async (section: keyof BusinessConfig, newSettings: any) => {
    if (!selectedCompany?.id || !config) return;

    const updatedConfig = {
      ...config,
      [section]: {
        ...config[section],
        ...newSettings
      }
    };

    try {
      await supabase
        .from('company_business_config')
        .upsert({
          company_id: selectedCompany.id,
          configuration: updatedConfig as any,
          is_active: true,
          updated_at: new Date().toISOString()
        }, { onConflict: 'company_id' });

      setConfig(updatedConfig);

      // Broadcast change to all connected clients
      const channel = supabase.channel(`business-config-${selectedCompany.id}`);
      channel.send({
        type: 'broadcast',
        event: 'config_update',
        payload: { section, newSettings, updatedConfig }
      });
    } catch (error) {
      console.error('Error updating business config:', error);
      throw error;
    }
  };

  // Setup realtime subscriptions
  useEffect(() => {
    if (!selectedCompany?.id || !config?.notification_settings.realtime_updates) return;

    const channels: any[] = [];

    // Stock updates channel
    const stockChannel = supabase
      .channel(`stock-updates-${selectedCompany.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'branch_stock',
          filter: `branch_id=in.(${selectedCompany.id})`
        },
        (payload) => {
          setRealtimeData(prev => ({
            ...prev,
            stock_updates: [payload, ...prev.stock_updates.slice(0, 49)]
          }));
        }
      )
      .subscribe();

    // Transfers channel
    const transferChannel = supabase
      .channel(`transfers-${selectedCompany.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'branch_transfers',
          filter: `company_id=eq.${selectedCompany.id}`
        },
        (payload) => {
          setRealtimeData(prev => ({
            ...prev,
            transfers: [payload, ...prev.transfers.slice(0, 49)]
          }));
        }
      )
      .subscribe();

    // Sales channel
    const salesChannel = supabase
      .channel(`sales-${selectedCompany.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sales',
          filter: `company_id=eq.${selectedCompany.id}`
        },
        (payload) => {
          setRealtimeData(prev => ({
            ...prev,
            sales: [payload, ...prev.sales.slice(0, 49)]
          }));
        }
      )
      .subscribe();

    // Configuration updates channel
    const configChannel = supabase
      .channel(`business-config-${selectedCompany.id}`)
      .on('broadcast', { event: 'config_update' }, (payload) => {
        setConfig(payload.updatedConfig);
      })
      .subscribe();

    channels.push(stockChannel, transferChannel, salesChannel, configChannel);

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [selectedCompany?.id, config?.notification_settings.realtime_updates]);

  useEffect(() => {
    loadConfig();
  }, [selectedCompany?.id]);

  return (
    <BusinessOperationsContext.Provider 
      value={{ 
        config, 
        loading, 
        updateConfig, 
        realtimeData 
      }}
    >
      {children}
    </BusinessOperationsContext.Provider>
  );
}

export function useBusinessOperations() {
  const context = useContext(BusinessOperationsContext);
  if (context === undefined) {
    throw new Error('useBusinessOperations must be used within a BusinessOperationsProvider');
  }
  return context;
}